import type {
  PublicHelpLocale,
  PublicHelpLocalizedText,
  PublicHelpResourceKind,
} from "./publicHelp";

/**
 * The help centre's one taxonomy (wiki/plans/help-center-lifecycle-facets-2026-10-27.md):
 * every help page has one canonical address and four facets — the planning
 * stages it belongs to (home-care terms, not Layer 1/2/3), the roles it serves,
 * the data objects it covers and its content type. A page may sit in several
 * stages; its primary home is its collection when it has one, else its first
 * stage. Replaces the former sections, planning stages and use cases.
 */

export type PublicHelpStageId =
  | "kom-igang"
  | "kommunbeslut"
  | "genomforandeplan"
  | "personal"
  | "planering"
  | "granska"
  | "publicerat"
  | "drift"
  | "uppfoljning";

export type PublicHelpCollectionId =
  | "installningar"
  | "ai"
  | "personalhandboken"
  | "integrationer"
  | "vanliga-fragor";

export type PublicHelpRoleId =
  | "samordnare"
  | "enhetschef"
  | "verksamhetschef"
  | "administrator"
  | "ekonomi"
  | "medarbetare"
  | "kund-anhorig"
  | "utvecklare";

export type PublicHelpResourceFacetId =
  | "organisation"
  | "enhet"
  | "distrikt"
  | "kund"
  | "kommunbeslut"
  | "besoksmall"
  | "medarbetare"
  | "arbetspass"
  | "schema"
  | "losning"
  | "besok"
  | "journal"
  | "handbokssida";

export type PublicHelpContentType = "guide" | "referens" | "begrepp" | "faq";

/** Brand 2.2 domain roles (packages/brand/presets.js `domainColors`). */
export type PublicHelpDomain =
  | "planning"
  | "care"
  | "continuity"
  | "attention"
  | "human"
  | "society"
  | "ai";

export interface PublicHelpFacets {
  stages: PublicHelpStageId[];
  collection?: PublicHelpCollectionId;
  roles: PublicHelpRoleId[];
  resources: PublicHelpResourceFacetId[];
  contentType: PublicHelpContentType;
}

export interface PublicHelpStage {
  id: PublicHelpStageId;
  number: number;
  slug: PublicHelpLocalizedText;
  title: PublicHelpLocalizedText;
  summary: PublicHelpLocalizedText;
  domain: PublicHelpDomain;
}

export interface PublicHelpCollection {
  id: PublicHelpCollectionId;
  slug: PublicHelpLocalizedText;
  title: PublicHelpLocalizedText;
  summary: PublicHelpLocalizedText;
  domain: PublicHelpDomain;
}

const l = (sv: string, en: string): PublicHelpLocalizedText => ({ sv, en });

export const PUBLIC_HELP_STAGES: readonly PublicHelpStage[] = [
  {
    id: "kom-igang",
    number: 0,
    slug: l("kom-igang", "getting-started"),
    title: l("Kom igång", "Getting started"),
    summary: l(
      "Konto, organisation, enheter, användare, inloggning och personuppgiftsbiträdesavtal.",
      "Account, organisation, units, users, sign-in and the data processing agreement.",
    ),
    domain: "society",
  },
  {
    id: "kommunbeslut",
    number: 1,
    slug: l("kommunbeslut", "municipal-decisions"),
    title: l("Kommunbeslut", "Municipal decisions"),
    summary: l(
      "Biståndsbeslut på kunden, import av beslut och uppehåll.",
      "Care decisions on the client, decision import and pauses.",
    ),
    domain: "continuity",
  },
  {
    id: "genomforandeplan",
    number: 2,
    slug: l("genomforandeplan", "care-plan"),
    title: l("Genomförandeplan", "Care plan"),
    summary: l(
      "Insatser och besöksmallar, rekommenderad dag och tid, tidsfönster och kontinuitet.",
      "Services and visit templates, recommended day and time, time windows and continuity.",
    ),
    domain: "human",
  },
  {
    id: "personal",
    number: 3,
    slug: l("personal-och-arbetspass", "staff-and-shifts"),
    title: l("Personal och arbetspass", "Staff and shifts"),
    summary: l(
      "Medarbetare, kompetenser, arbetspassmallar, veckoschema och distrikt.",
      "Employees, skills, shift templates, weekly roster and districts.",
    ),
    domain: "care",
  },
  {
    id: "planering",
    number: 4,
    slug: l("besoks-och-skiftplanering", "visit-and-shift-planning"),
    title: l("Besöks- och skiftplanering", "Visit and shift planning"),
    summary: l(
      "Caire planerar kommande veckor automatiskt. Skapa och optimera scheman.",
      "Caire plans upcoming weeks automatically. Create and optimise schedules.",
    ),
    domain: "planning",
  },
  {
    id: "granska",
    number: 5,
    slug: l("granska-och-forbattra", "review-and-improve"),
    title: l("Granska och förbättra", "Review and improve"),
    summary: l(
      "Analys, kalender, jämför lösningar, diagnostik och rapporter.",
      "Analysis, calendar, compare solutions, diagnostics and reports.",
    ),
    domain: "attention",
  },
  {
    id: "publicerat",
    number: 6,
    slug: l("publicerat-schema", "published-schedule"),
    title: l("Publicerat schema", "Published schedule"),
    summary: l(
      "Publicera till medarbetar-, kund- och anhörigappen.",
      "Publish to the employee, client and family apps.",
    ),
    domain: "planning",
  },
  {
    id: "drift",
    number: 7,
    slug: l("dagen-i-drift", "the-working-day"),
    title: l("Dagen i drift", "The working day"),
    summary: l(
      "Sjukanmälningar, ändrade besök, avbokningar, förseningar och omplanering.",
      "Sick calls, changed visits, cancellations, delays and replanning.",
    ),
    domain: "human",
  },
  {
    id: "uppfoljning",
    number: 8,
    slug: l("dokumentation-och-uppfoljning", "documentation-and-follow-up"),
    title: l("Dokumentation och uppföljning", "Documentation and follow-up"),
    summary: l(
      "Journal, avvikelser, plan mot utfört, fakturering och jämförelser.",
      "Journal, deviations, plan versus performed, billing and benchmarks.",
    ),
    domain: "continuity",
  },
];

export const PUBLIC_HELP_COLLECTIONS: readonly PublicHelpCollection[] = [
  {
    id: "installningar",
    slug: l("installningar", "settings"),
    title: l("Inställningar", "Settings"),
    summary: l(
      "Vilken inställning som gäller, från organisation till besök.",
      "Which setting applies, from organisation down to the visit.",
    ),
    domain: "society",
  },
  {
    id: "ai",
    slug: l("ai", "ai"),
    title: l("AI i Caire", "AI in Caire"),
    summary: l(
      "Fråga CAIRE i schemat och AI-stöd i appen.",
      "Ask CAIRE in the schedule and AI support in the app.",
    ),
    domain: "ai",
  },
  {
    id: "personalhandboken",
    slug: l("personalhandboken", "employee-handbook"),
    title: l("Personalhandboken", "Employee handbook"),
    summary: l(
      "Administrera mallar, sidor, introduktion och kontakter. Läs handboken i appen.",
      "Manage templates, pages, onboarding and contacts. Read the handbook in the app.",
    ),
    domain: "care",
  },
  {
    id: "integrationer",
    slug: l("integrationer", "integrations"),
    title: l("Integrationer", "Integrations"),
    summary: l(
      "Partner-API, webhookar och kopplingar. Tekniska guider finns under Utvecklare.",
      "Partner API, webhooks and connectors. Technical guides live under Developers.",
    ),
    domain: "society",
  },
  {
    id: "vanliga-fragor",
    slug: l("vanliga-fragor", "faq"),
    title: l("Vanliga frågor", "FAQ"),
    summary: l(
      "Snabba svar och kontakt med Caire.",
      "Quick answers and how to reach Caire.",
    ),
    domain: "human",
  },
];

export const PUBLIC_HELP_ROLES: readonly {
  id: PublicHelpRoleId;
  title: PublicHelpLocalizedText;
}[] = [
  { id: "samordnare", title: l("Samordnare", "Coordinator") },
  { id: "enhetschef", title: l("Enhetschef", "Unit manager") },
  { id: "verksamhetschef", title: l("Verksamhetschef", "Operations manager") },
  { id: "administrator", title: l("Administratör", "Administrator") },
  { id: "ekonomi", title: l("Ekonomi", "Finance") },
  { id: "medarbetare", title: l("Medarbetare i appen", "Employee in the app") },
  { id: "kund-anhorig", title: l("Kund och anhörig", "Client and family") },
  { id: "utvecklare", title: l("Utvecklare", "Developer") },
];

export const PUBLIC_HELP_RESOURCE_FACETS: readonly {
  id: PublicHelpResourceFacetId;
  title: PublicHelpLocalizedText;
}[] = [
  { id: "organisation", title: l("Organisation", "Organisation") },
  { id: "enhet", title: l("Enhet", "Unit") },
  { id: "distrikt", title: l("Distrikt", "District") },
  { id: "kund", title: l("Kund", "Client") },
  { id: "kommunbeslut", title: l("Kommunbeslut", "Municipal decision") },
  { id: "besoksmall", title: l("Besöksmall", "Visit template") },
  { id: "medarbetare", title: l("Medarbetare", "Employee") },
  { id: "arbetspass", title: l("Arbetspass", "Shift") },
  { id: "schema", title: l("Schema", "Schedule") },
  { id: "losning", title: l("Lösning", "Solution") },
  { id: "besok", title: l("Besök", "Visit") },
  { id: "journal", title: l("Journal", "Journal") },
  { id: "handbokssida", title: l("Handbokssida", "Handbook page") },
];

export const PUBLIC_HELP_CONTENT_TYPES: readonly {
  id: PublicHelpContentType;
  title: PublicHelpLocalizedText;
}[] = [
  { id: "guide", title: l("Guide", "Guide") },
  { id: "referens", title: l("Referens", "Reference") },
  { id: "begrepp", title: l("Begrepp", "Concept") },
  { id: "faq", title: l("Vanliga frågor", "FAQ") },
];

const f = (
  stages: PublicHelpStageId[],
  roles: PublicHelpRoleId[],
  resources: PublicHelpResourceFacetId[],
  contentType: PublicHelpContentType = "guide",
  collection?: PublicHelpCollectionId,
): PublicHelpFacets => ({ stages, roles, resources, contentType, collection });

/** Facets of the guides registered in publicHelp.ts and its page modules. */
export const PUBLIC_HELP_PAGE_FACETS: Readonly<
  Record<string, PublicHelpFacets>
> = {
  platformBasicsDataOrder: f(
    ["kom-igang", "planering"],
    ["samordnare", "verksamhetschef"],
    ["schema"],
  ),
  securityAndPersonalData: f(
    ["kom-igang"],
    ["administrator"],
    ["organisation"],
    "begrepp",
  ),
  careDecisions: f(
    ["kommunbeslut"],
    ["samordnare", "enhetschef"],
    ["kund", "kommunbeslut"],
  ),
  clientsContinuity: f(
    ["genomforandeplan", "granska"],
    ["samordnare", "enhetschef"],
    ["kund"],
  ),
  visitsTimeWindowsAndChains: f(
    ["genomforandeplan", "granska", "drift"],
    ["samordnare"],
    ["besoksmall", "besok"],
  ),
  serviceAreaDemandInsetFlex: f(
    ["genomforandeplan", "planering"],
    ["samordnare", "enhetschef"],
    ["enhet", "besoksmall"],
  ),
  employeesEffectiveInputs: f(
    ["personal"],
    ["samordnare", "ekonomi"],
    ["medarbetare"],
  ),
  districts: f(
    ["personal", "planering"],
    ["samordnare", "administrator"],
    ["enhet", "distrikt"],
  ),
  walkingDistricts: f(
    ["personal"],
    ["samordnare"],
    ["distrikt", "medarbetare"],
  ),
  optimizationReadiness: f(
    ["planering", "genomforandeplan", "personal"],
    ["samordnare"],
    ["kund", "medarbetare"],
  ),
  createSchedule: f(["planering"], ["samordnare"], ["schema"]),
  firstScheduleImprovementLoop: f(
    ["planering", "granska"],
    ["samordnare"],
    ["schema", "losning"],
  ),
  analysReadAndFixDraft: f(
    ["granska"],
    ["samordnare", "enhetschef"],
    ["losning"],
  ),
  solutions: f(["granska"], ["samordnare", "enhetschef"], ["losning"]),
  scheduleDiagnostics: f(["granska"], ["samordnare"], ["losning"]),
  planReality: f(
    ["granska", "uppfoljning"],
    ["enhetschef", "verksamhetschef"],
    ["losning", "besok"],
  ),
  publishSchedule: f(["publicerat"], ["samordnare"], ["schema"]),
  mobileAppTour: f(
    ["publicerat", "drift"],
    ["medarbetare", "kund-anhorig"],
    ["besok"],
  ),
  sickCallsAndStaffing: f(
    ["drift"],
    ["samordnare", "medarbetare"],
    ["medarbetare", "arbetspass"],
  ),
  journal: f(
    ["uppfoljning"],
    ["enhetschef", "medarbetare"],
    ["journal", "kund"],
  ),
  scheduleAiAssistant: f(
    ["granska", "drift"],
    ["samordnare", "enhetschef"],
    ["schema", "losning"],
    "guide",
    "ai",
  ),
};

/** Facets of the field-reference pages (`/hjalp/data/*`). */
export const PUBLIC_HELP_RESOURCE_PAGE_FACETS: Readonly<
  Partial<Record<PublicHelpResourceKind, PublicHelpFacets>>
> = {
  organization: f(
    ["kom-igang"],
    ["administrator"],
    ["organisation"],
    "referens",
  ),
  operationalSettings: f(
    ["genomforandeplan", "personal", "planering"],
    ["administrator", "samordnare"],
    ["organisation", "enhet"],
    "referens",
    "installningar",
  ),
  serviceArea: f(
    ["kom-igang", "personal"],
    ["administrator", "samordnare"],
    ["enhet"],
    "referens",
  ),
  district: f(["personal"], ["samordnare"], ["distrikt"], "referens"),
  employees: f(
    ["personal", "planering", "drift"],
    ["samordnare"],
    ["medarbetare", "arbetspass"],
    "referens",
  ),
  clients: f(
    ["kommunbeslut", "genomforandeplan"],
    ["samordnare"],
    ["kund"],
    "referens",
  ),
  visitTemplates: f(
    ["genomforandeplan", "planering", "granska", "drift"],
    ["samordnare"],
    ["besoksmall"],
    "referens",
  ),
  schedules: f(
    ["planering", "publicerat"],
    ["samordnare"],
    ["schema"],
    "referens",
  ),
  solutions: f(
    ["granska", "publicerat"],
    ["samordnare", "enhetschef"],
    ["losning"],
    "referens",
  ),
  diagnostics: f(["granska"], ["samordnare"], ["losning"], "referens"),
};

export function publicHelpStage(id: PublicHelpStageId): PublicHelpStage {
  return PUBLIC_HELP_STAGES.find((stage) => stage.id === id)!;
}

export function publicHelpCollection(
  id: PublicHelpCollectionId,
): PublicHelpCollection {
  return PUBLIC_HELP_COLLECTIONS.find((collection) => collection.id === id)!;
}

export function stageBySlug(
  slug: string,
  locale: PublicHelpLocale,
): PublicHelpStage | null {
  return (
    PUBLIC_HELP_STAGES.find((stage) => stage.slug[locale] === slug) ?? null
  );
}

export function collectionBySlug(
  slug: string,
  locale: PublicHelpLocale,
): PublicHelpCollection | null {
  return (
    PUBLIC_HELP_COLLECTIONS.find(
      (collection) => collection.slug[locale] === slug,
    ) ?? null
  );
}

/** The page's one home: its collection, else its first stage. */
export function primaryHelpHome(
  facets: PublicHelpFacets,
):
  | { kind: "collection"; id: PublicHelpCollectionId }
  | { kind: "stage"; id: PublicHelpStageId } {
  return facets.collection
    ? { kind: "collection", id: facets.collection }
    : { kind: "stage", id: facets.stages[0]! };
}
